import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { 
  Clock, 
  Brain, 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  Calendar,
  Target,
  Zap,
  BookOpen,
  Star
} from "lucide-react";

function SpacedRepetitionSystem() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0 });

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          setUserData(data);
          
          // Generate review items based on spaced repetition algorithm
          const items = generateReviewItems(data);
          setReviewItems(items);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Spaced repetition intervals (in days)
  const intervals = [1, 3, 7, 14, 30, 90, 180];

  const generateReviewItems = (data) => {
    const items = [];
    const now = new Date();
    
    // Get vocabulary progress
    const vocabProgress = data.vocabularyProgress || {};
    
    // Get completed lessons
    const completedLessons = data.completedLessons || [];
    
    // Generate review items for vocabulary
    Object.entries(vocabProgress).forEach(([word, progress]) => {
      const lastReview = progress.lastReview ? new Date(progress.lastReview) : new Date();
      const daysSinceReview = Math.floor((now - lastReview) / (1000 * 60 * 60 * 24));
      const currentInterval = intervals[progress.intervalIndex || 0];
      
      if (daysSinceReview >= currentInterval) {
        items.push({
          type: 'vocabulary',
          word: word,
          korean: progress.korean || word,
          english: progress.english || 'Unknown',
          roman: progress.roman || '',
          difficulty: progress.difficulty || 'medium',
          intervalIndex: progress.intervalIndex || 0,
          correctStreak: progress.correctStreak || 0,
          lastReview: progress.lastReview,
          priority: calculatePriority(daysSinceReview, currentInterval, progress.correctStreak)
        });
      }
    });

    // Generate review items for lessons
    completedLessons.forEach(lessonId => {
      const lessonProgress = data.lessonProgress?.[lessonId] || {};
      const lastReview = lessonProgress.lastReview ? new Date(lessonProgress.lastReview) : new Date();
      const daysSinceReview = Math.floor((now - lastReview) / (1000 * 60 * 60 * 24));
      const currentInterval = intervals[lessonProgress.intervalIndex || 0];
      
      if (daysSinceReview >= currentInterval) {
        items.push({
          type: 'lesson',
          lessonId: lessonId,
          title: lessonProgress.title || `Lesson ${lessonId}`,
          content: lessonProgress.content || 'Review lesson content',
          intervalIndex: lessonProgress.intervalIndex || 0,
          correctStreak: lessonProgress.correctStreak || 0,
          lastReview: lessonProgress.lastReview,
          priority: calculatePriority(daysSinceReview, currentInterval, lessonProgress.correctStreak)
        });
      }
    });

    // Sort by priority (highest first)
    return items.sort((a, b) => b.priority - a.priority);
  };

  const calculatePriority = (daysSinceReview, currentInterval, correctStreak) => {
    // Higher priority for items that are overdue
    const overdueFactor = Math.max(0, daysSinceReview - currentInterval);
    
    // Lower priority for items with high correct streaks
    const streakFactor = Math.max(0, 5 - correctStreak);
    
    return overdueFactor * 10 + streakFactor * 5;
  };

  const handleAnswer = async (correct) => {
    if (!currentUser || !reviewItems[currentItem]) return;

    const item = reviewItems[currentItem];
    const newStats = { ...sessionStats };
    newStats.total++;
    
    if (correct) {
      newStats.correct++;
      
      // Update interval based on performance
      const newIntervalIndex = Math.min(item.intervalIndex + 1, intervals.length - 1);
      const newCorrectStreak = item.correctStreak + 1;
      
      // Update in Firebase
      await updateItemProgress(item, newIntervalIndex, newCorrectStreak, true);
    } else {
      // Reset interval on incorrect answer
      const newIntervalIndex = 0;
      const newCorrectStreak = 0;
      
      await updateItemProgress(item, newIntervalIndex, newCorrectStreak, false);
    }
    
    setSessionStats(newStats);
    
    // Move to next item or complete session
    if (currentItem < reviewItems.length - 1) {
      setCurrentItem(currentItem + 1);
      setShowAnswer(false);
    } else {
      setSessionComplete(true);
    }
  };

  const updateItemProgress = async (item, intervalIndex, correctStreak, correct) => {
    try {
      const userRef = doc(db, "users", currentUser.uid);
      const now = new Date().toISOString();
      
      if (item.type === 'vocabulary') {
        await updateDoc(userRef, {
          [`vocabularyProgress.${item.word}`]: {
            ...userData.vocabularyProgress[item.word],
            intervalIndex,
            correctStreak,
            lastReview: now,
            totalReviews: (userData.vocabularyProgress[item.word]?.totalReviews || 0) + 1,
            correctAnswers: (userData.vocabularyProgress[item.word]?.correctAnswers || 0) + (correct ? 1 : 0)
          }
        });
      } else if (item.type === 'lesson') {
        await updateDoc(userRef, {
          [`lessonProgress.${item.lessonId}`]: {
            ...userData.lessonProgress?.[item.lessonId],
            intervalIndex,
            correctStreak,
            lastReview: now,
            totalReviews: (userData.lessonProgress?.[item.lessonId]?.totalReviews || 0) + 1,
            correctAnswers: (userData.lessonProgress?.[item.lessonId]?.correctAnswers || 0) + (correct ? 1 : 0)
          }
        });
      }
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const resetSession = () => {
    setCurrentItem(0);
    setShowAnswer(false);
    setSessionComplete(false);
    setSessionStats({ correct: 0, total: 0 });
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (reviewItems.length === 0) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl shadow-2xl p-8 border-4 border-green-200 text-center">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-3xl font-black text-gray-900 mb-4">All Caught Up!</h2>
        <p className="text-lg text-gray-700 mb-6">
          Great job! You have no items scheduled for review right now.
        </p>
        <div className="text-sm text-gray-600">
          Come back tomorrow for your next review session.
        </div>
      </div>
    );
  }

  if (sessionComplete) {
    const accuracy = Math.round((sessionStats.correct / sessionStats.total) * 100);
    
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-2xl p-8 border-4 border-blue-200 text-center">
        <Star className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <h2 className="text-3xl font-black text-gray-900 mb-4">Review Complete!</h2>
        
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-lg">
            <div className="text-2xl font-bold text-green-600">{sessionStats.correct}</div>
            <div className="text-sm text-gray-600">Correct</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-lg">
            <div className="text-2xl font-bold text-blue-600">{accuracy}%</div>
            <div className="text-sm text-gray-600">Accuracy</div>
          </div>
        </div>
        
        <p className="text-lg text-gray-700 mb-6">
          {accuracy >= 80 
            ? "Excellent work! You're mastering Korean!"
            : accuracy >= 60 
            ? "Good job! Keep practicing to improve."
            : "Keep studying! Practice makes perfect."
          }
        </p>
        
        <button
          onClick={resetSession}
          className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-3 rounded-xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Review Again
        </button>
      </div>
    );
  }

  const currentReviewItem = reviewItems[currentItem];

  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Brain className="w-8 h-8 text-red-600 mr-3" />
          <h2 className="text-3xl font-black text-gray-900">Smart Review</h2>
        </div>
        
        <div className="flex justify-center space-x-4 mb-4">
          <div className="bg-white rounded-xl p-3 shadow-lg">
            <div className="text-sm text-gray-600">Progress</div>
            <div className="text-lg font-bold text-red-600">
              {currentItem + 1} / {reviewItems.length}
            </div>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-lg">
            <div className="text-sm text-gray-600">Accuracy</div>
            <div className="text-lg font-bold text-green-600">
              {sessionStats.total > 0 ? Math.round((sessionStats.correct / sessionStats.total) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Review Item */}
      <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 mb-8">
        {currentReviewItem.type === 'vocabulary' ? (
          <div className="text-center">
            <div className="text-4xl font-bold text-red-700 mb-4">
              {currentReviewItem.korean}
            </div>
            <div className="text-lg text-gray-600 mb-6">
              {currentReviewItem.roman}
            </div>
            
            {!showAnswer ? (
              <button
                onClick={() => setShowAnswer(true)}
                className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-3 rounded-xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Show Answer
              </button>
            ) : (
              <div className="space-y-6">
                <div className="text-2xl font-bold text-gray-900">
                  {currentReviewItem.english}
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => handleAnswer(false)}
                    className="flex items-center px-6 py-3 bg-red-100 text-red-700 rounded-xl font-bold hover:bg-red-200 transition-all duration-300"
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Incorrect
                  </button>
                  <button
                    onClick={() => handleAnswer(true)}
                    className="flex items-center px-6 py-3 bg-green-100 text-green-700 rounded-xl font-bold hover:bg-green-200 transition-all duration-300"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Correct
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 mb-4">
              {currentReviewItem.title}
            </div>
            <div className="text-lg text-gray-700 mb-6">
              {currentReviewItem.content}
            </div>
            
            {!showAnswer ? (
              <button
                onClick={() => setShowAnswer(true)}
                className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-3 rounded-xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Show Answer
              </button>
            ) : (
              <div className="space-y-6">
                <div className="text-lg text-gray-700">
                  Review the lesson content and test your understanding.
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => handleAnswer(false)}
                    className="flex items-center px-6 py-3 bg-red-100 text-red-700 rounded-xl font-bold hover:bg-red-200 transition-all duration-300"
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Need More Practice
                  </button>
                  <button
                    onClick={() => handleAnswer(true)}
                    className="flex items-center px-6 py-3 bg-green-100 text-green-700 rounded-xl font-bold hover:bg-green-200 transition-all duration-300"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Understood
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div 
          className="h-3 rounded-full bg-gradient-to-r from-red-600 to-orange-600 transition-all duration-500"
          style={{ width: `${((currentItem + 1) / reviewItems.length) * 100}%` }}
        ></div>
      </div>
      
      <div className="text-center text-sm text-gray-600">
        {currentItem + 1} of {reviewItems.length} items completed
      </div>
    </div>
  );
}

export default SpacedRepetitionSystem;

